import React, { Fragment, useCallback, useContext, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Alert,
  Modal,
  PanResponder,
  Pressable,
  ScrollView as RNScrollView,
  Text,
  useWindowDimensions,
  View
} from 'react-native'
import { ApolloError, useMutation, useQuery, type Reference } from '@apollo/client'
import { differenceInMilliseconds, format, parseISO } from 'date-fns'
import { Image } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import { useRouter, type Href } from 'expo-router'
import { BigNumber } from 'mathjs'
import { useTranslation } from 'react-i18next'
import { GestureHandlerRootView, ScrollView as GestureScrollView } from 'react-native-gesture-handler'
import { useSharedValue } from 'react-native-reanimated'
import { Carousel as RNCarousel } from 'react-native-reanimated-carousel'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { basic } from '@/constants/colors'
import { brandTheme } from '@/config/brandTheme'
import { formatLocalFiatAmount, marketPresentation } from '@/config/marketPresentation'
import { SUPPORTED_CURRENCIES, WEB_VIEW_MAX_WIDTH } from '@/constants/config'
import { REDEEM_PROMO_TICKET } from '@/constants/graphql/mutations'
import { GET_BONUS_ACTIVITY_CONNECTION, GET_WALLETS, MY_PROMO_TICKETS } from '@/constants/graphql/queries'
import { CurrencyKind, Wallet } from '@/types/wallet'
import { cn } from '@/utils/cn'
import { getBignumber, toCurrency } from '@/utils/numbers'
import { AuthContext } from '@/stores/auth'
import CustomerServiceButton from '@/components/CustomerServiceButton'
import {
  ConvertOutline,
  CustomerService,
  Deposit,
  RedEnvelope,
  Scan,
  Ticket,
  TransformFilled
} from '@/components/icon'
import { Stack, TabScreenContent } from '@/components/layout'
import NotificationButton from '@/components/NotificationButton'
import BrandLogo from '@/components/BrandLogo'
import { RedeemResult } from '@/components/orders/child/CompletedScreen'
import { CustomerServiceContext } from '@/components/providers/CustomerService'
import ScreenHeader from '@/components/ScreenHeader'

type Summary = {
  total: BigNumber
  available: BigNumber
  frozen: BigNumber
  bonus: BigNumber
}

function defaultSummary(): Summary {
  return {
    total: getBignumber(0),
    available: getBignumber(0),
    frozen: getBignumber(0),
    bonus: getBignumber(0)
  }
}

type PromoTicket = {
  id: string
  promoId: string
  promoCode: string
  promoVersion: number
  issuedAt: string
  sourceEventId: string
  issueKey: string
}

type PromoRedeemResult = {
  ticketId: string
  rewardOutcome: string
  rewardAmount?: string | null
  rewardLabel?: string | null
  alreadyRedeemed: boolean
}

function PromoTicketButton({ hasTickets, onPress }: { hasTickets: boolean; onPress: () => void }) {
  return (
    <Pressable className={'size-10 items-center justify-center'} onPress={onPress}>
      {({ pressed }) => (
        <>
          {hasTickets && <View className={'absolute right-1.5 top-1.5 size-2 rounded-full bg-danger'} />}
          <Ticket className={cn('size-6 text-white', pressed && 'opacity-50')} />
        </>
      )}
    </Pressable>
  )
}

type PromoTicketsModalProps = {
  visible: boolean
  onClose: () => void
  tickets: PromoTicket[]
  loading: boolean
  error?: ApolloError
  onRedeem: (ticketId: string) => void
  redeemingTicketId: string | null
  redeemResult: PromoRedeemResult | null
  onDismissResult: () => void
}

function PromoTicketsModal({
  visible,
  onClose,
  tickets,
  loading,
  error,
  onRedeem,
  redeemingTicketId,
  redeemResult,
  onDismissResult
}: PromoTicketsModalProps) {
  const { t } = useTranslation()
  const resultMessage = redeemResult
    ? redeemResult.alreadyRedeemed
      ? t('home:ticketUsed')
      : redeemResult.rewardOutcome !== 'NoPrize' && redeemResult.rewardAmount
        ? t('home:ticketWin', {
            amount: redeemResult.rewardAmount,
            label: redeemResult.rewardLabel ? `（${redeemResult.rewardLabel}）` : ''
          })
        : t('home:ticketNoPrize')
    : null

  return (
    <Modal visible={visible} transparent statusBarTranslucent>
      <Pressable className={'absolute inset-0 bg-black opacity-50'} onPress={onClose} />
      <View
        className={
          'absolute inset-x-0 bottom-0 mx-auto w-full max-w-[540px] rounded-t-3xl bg-background-primary px-4 pb-8 pt-5'
        }
      >
        <View className={'items-center'}>
          <View className={'mb-4 h-1 w-10 rounded-full bg-border-10'} />
        </View>
        <View className={'mb-4 flex-row items-center justify-between'}>
          <Text className={'text-lg font-medium text-text-primary'}>{t('home:tickets')}</Text>
          <Pressable onPress={onClose} className={'m-[-16px] p-4'}>
            <Text className={'text-sm text-primary'}>{t('home:close')}</Text>
          </Pressable>
        </View>
        {redeemResult && resultMessage && <RedeemResult redeemResult={redeemResult} handleClick={onDismissResult} />}
        {loading ? (
          <View className={'items-center justify-center py-10'}>
            <ActivityIndicator />
          </View>
        ) : error ? (
          <Text className={'text-sm text-text-secondary'}>{t('home:ticketLoadFailed')}</Text>
        ) : tickets.length === 0 ? (
          <Text className={'text-sm text-text-secondary'}>{t('home:noTickets')}</Text>
        ) : (
          <RNScrollView style={{ maxHeight: 360 }} showsVerticalScrollIndicator={false}>
            <View className={'gap-3'}>
              {tickets.map((ticket) => {
                let issuedAtText = ticket.issuedAt
                try {
                  issuedAtText = format(parseISO(ticket.issuedAt), 'yyyy-MM-dd HH:mm')
                } catch {
                  // ignore parse errors and fall back to raw value
                }
                return (
                  <Pressable
                    key={ticket.id}
                    className={'rounded-2xl border border-border-10 bg-background-secondary px-4 py-3'}
                    onPress={() => onRedeem(ticket.id)}
                  >
                    <View className={'mt-1 flex-row items-center gap-2'}>
                      <RedEnvelope />
                      <View className={'flex flex-col'}>
                        <Text className={'text-xs text-text-tertiary'}>{issuedAtText}</Text>
                        <Text className={'text-base font-medium text-text-primary'}>
                          {t('home:unclaimedTicket')}
                        </Text>
                      </View>
                      <Text className={'ml-auto rounded-lg bg-primary px-1.5 py-2 text-xs text-white'}>
                        {t('home:drawNow')}
                      </Text>
                    </View>
                  </Pressable>
                )
              })}
            </View>
          </RNScrollView>
        )}
      </View>
    </Modal>
  )
}

type MyPromoTicketsQuery = {
  myPromoTickets: PromoTicket[]
}

type RedeemPromoTicketMutation = {
  redeemPromoTicket: {
    ticketId: string
    promoId: string
    promoCode: string
    promoVersion: number
    rewardAmount?: string | null
    rewardLabel?: string | null
    rewardOutcome: string
    roll?: number | null
    alreadyRedeemed: boolean
  }
}

type RedeemPromoTicketVariables = {
  ticketId: string
}

export default function HomeScreen() {
  const { t } = useTranslation()
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const { data: walletData, loading: walletLoading } = useQuery(GET_WALLETS, {
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    skip: !user?.id,
    onError: (error) => console.log(`get wallet failed, ${error}`)
  })

  const { data: activityData } = useQuery(GET_BONUS_ACTIVITY_CONNECTION, {
    variables: { last: 20 },
    skip: !user?.id
  })

  const wallets = walletData ? (walletData.getWallets?.filter((w: Wallet) => w.balance.kind === 'YBB') ?? []) : []
  const currentSummary = walletData ? getSummary(walletData) : defaultSummary()
  const activities = activityData ? getActiveActivity(activityData) : []

  const [ticketsVisible, setTicketsVisible] = useState(false)
  const [redeemingTicketId, setRedeemingTicketId] = useState<string | null>(null)
  const [redeemResultDetails, setRedeemResultDetails] = useState<PromoRedeemResult | null>(null)

  const {
    data: ticketsData,
    loading: ticketsLoading,
    error: ticketsError,
    refetch: refetchTickets
  } = useQuery<MyPromoTicketsQuery>(MY_PROMO_TICKETS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60 * 1000,
    errorPolicy: 'all',
    skip: !user?.id
  })

  const tickets = ticketsData?.myPromoTickets ?? []
  const [dismissedTicketIds, setDismissedTicketIds] = useState<Set<string>>(new Set())
  const filteredTickets = useMemo(
    () => tickets.filter((ticket) => !dismissedTicketIds.has(ticket.id)),
    [tickets, dismissedTicketIds]
  )
  const hasTickets = filteredTickets.length > 0

  const getGraphQlErrorMessage = (errors?: readonly { message?: string }[]) =>
    errors
      ?.map((error) => error.message?.trim())
      .filter((message): message is string => Boolean(message && message.length > 0))
      .join('\n')

  const [redeemTicketMutation] = useMutation<RedeemPromoTicketMutation, RedeemPromoTicketVariables>(
    REDEEM_PROMO_TICKET,
    {
      errorPolicy: 'all',
      update: (cache, { data }) => {
        const redeemedId = data?.redeemPromoTicket?.ticketId
        if (!redeemedId) {
          return
        }

        const cacheId = cache.identify({ __typename: 'UserPromoTicketType', id: redeemedId })
        if (cacheId) {
          cache.evict({ id: cacheId })
        }

        cache.modify({
          fields: {
            myPromoTickets(existingRefs: readonly Reference[] = [], { readField }) {
              return existingRefs.filter((ref: Reference) => readField('id', ref) !== redeemedId)
            }
          }
        })

        cache.gc()
      }
    }
  )

  const handleOpenTickets = useCallback(() => {
    if (!user?.id) {
      Alert.alert(t('c2c:notice'), t('home:loginToViewTickets'))
      return
    }
    setRedeemResultDetails(null)
    setTicketsVisible(true)
    refetchTickets().catch(() => undefined)
  }, [refetchTickets, user?.id])

  const screenOptions = useMemo(
    () => ({
      header: () => <Header onOpenTickets={handleOpenTickets} hasTickets={hasTickets} />
    }),
    [handleOpenTickets, hasTickets]
  )

  const handleCloseTickets = () => {
    setTicketsVisible(false)
    setRedeemResultDetails(null)
  }

  const redeemingTicketRef = useRef(false)

  const handleRedeem = async (ticketId: string) => {
    if (redeemingTicketRef.current) {
      return
    }
    try {
      redeemingTicketRef.current = true
      setRedeemingTicketId(ticketId)
      const { data, errors } = await redeemTicketMutation({
        variables: { ticketId }
      })
      const graphQlMessage = getGraphQlErrorMessage(errors)
      if (graphQlMessage) {
        throw new Error(graphQlMessage)
      }

      const result = data?.redeemPromoTicket
      if (!result) {
        throw new Error(t('home:drawResultUnavailable'))
      }

      const nextDismissed = new Set(dismissedTicketIds)
      nextDismissed.add(result.ticketId)
      setDismissedTicketIds(nextDismissed)
      setRedeemResultDetails({
        ticketId: result.ticketId,
        rewardOutcome: result.rewardOutcome,
        rewardAmount: result.rewardAmount,
        rewardLabel: result.rewardLabel,
        alreadyRedeemed: result.alreadyRedeemed
      })

      const refreshed = await refetchTickets().catch(() => undefined)
      const remaining = (refreshed?.data?.myPromoTickets ?? []).filter((ticket) => !nextDismissed.has(ticket.id)).length
      if (remaining === 0) {
        setDismissedTicketIds(nextDismissed)
      }
    } catch (err) {
      let message = t('home:drawFailed')
      if (err instanceof ApolloError) {
        message = err.message.replace('GraphQL error: ', '')
      } else if (err instanceof Error) {
        message = err.message
      }
      Alert.alert(t('home:drawFailedTitle'), message)
    } finally {
      setRedeemingTicketId(null)
      redeemingTicketRef.current = false
    }
  }

  return (
    <>
      <Stack.Screen options={screenOptions} />

      <TabScreenContent>
        <SafeAreaView className={'flex-1'}>
          <GestureScrollView contentContainerStyle={{ paddingBottom: 16 }}>
            <SummaryPanel summary={currentSummary} loading={walletLoading && !walletData} />
            <Carousel activities={activities} />
            <AssetList wallets={wallets} loading={walletLoading && !walletData} />
          </GestureScrollView>
        </SafeAreaView>
      </TabScreenContent>
      <PromoTicketsModal
        visible={ticketsVisible}
        onClose={handleCloseTickets}
        tickets={filteredTickets}
        loading={ticketsLoading && !ticketsData}
        error={ticketsError}
        onRedeem={handleRedeem}
        redeemingTicketId={redeemingTicketId}
        redeemResult={redeemResultDetails}
        onDismissResult={() => setRedeemResultDetails(null)}
      />
    </>
  )
}

function getSummary(data: Record<string, any>) {
  const newWallets = data?.getWallets?.filter((w: Wallet) => w.balance.kind === 'YBB') ?? []
  return newWallets.reduce((acc: Summary, wallet: Wallet) => {
    const balance = getBignumber(wallet.balance.value)
    const bonusBalance = getBignumber(wallet.bonusBalance?.value ?? '0')
    const frozenBalance = getBignumber(wallet.frozenBalance.value)
    const lockedBalance = getBignumber(wallet.lockedBalance.value)

    return {
      total: acc.total.add(balance).add(frozenBalance).add(lockedBalance).add(bonusBalance),
      available: acc.available.add(balance),
      frozen: acc.frozen.add(frozenBalance).add(lockedBalance),
      bonus: acc.bonus.add(bonusBalance)
    }
  }, defaultSummary())
}

function getActiveActivity(data: Record<string, any>) {
  return data.getBonusActivityConnection.edges.filter(
    ({ node }: { node: Record<string, any> }) => differenceInMilliseconds(new Date(), new Date(node.endedAt)) < 0
  )
}

function Header({ onOpenTickets, hasTickets }: { onOpenTickets: () => void; hasTickets: boolean }) {
  const router = useRouter()
  const { top } = useSafeAreaInsets()

  return (
    <View className={'bg-background-brand'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={<CustomerServiceButton isHome={true} />}
        centerElement={<BrandLogo variant={'wordmark'} />}
        rightElement={
          <>
            <Pressable className={'size-10 items-center justify-center'} onPress={() => router.push('/scan2pay')}>
              {({ pressed }) => <Scan className={cn('size-6 text-white', pressed && 'opacity-50')} />}
            </Pressable>
            <PromoTicketButton hasTickets={hasTickets} onPress={onOpenTickets} />
            <NotificationButton isHome={true} />
          </>
        }
      />
    </View>
  )
}

function SummaryPanel({ summary, loading }: { summary: Summary; loading: boolean }) {
  const { t } = useTranslation()
  const router = useRouter()
  const { openChat } = useContext(CustomerServiceContext)

  const actions = [
    { method: 'deposit', icon: Deposit, pathname: '/deposit/' },
    { method: 'convert', icon: ConvertOutline, pathname: '(tabs)/convert/' },
    { method: 'buySellOrder', icon: TransformFilled, pathname: '(tabs)/c2c/' },
    { method: 'customerService', icon: CustomerService, action: openChat }
  ]

  return (
    <View className={'w-full bg-primary'}>
      <View className={'mx-4 my-2 mb-4 rounded-lg border border-border-10 bg-white'}>
        {/* summary */}
        <View className={'w-full gap-4 p-6 pb-4'}>
          <View className={'gap-1'}>
            <Text className={'text-sm text-text-secondary'}>{t('home:total')}</Text>
            <Text
              className={'font-roboto-semibold text-2xl text-text-primary'}
            >{formatLocalFiatAmount(loading ? '−.−−' : summary.total.toString())}</Text>
          </View>
          <View className={'flex-row gap-2'}>
            <View className={'flex-1 gap-1'}>
              <Text className={'text-xs text-text-secondary'}>{t('home:available')}</Text>
              <Text
                className={'font-roboto-medium text-xs text-text-primary'}
              >{formatLocalFiatAmount(loading ? '−.−−' : summary.available.toString())}</Text>
            </View>
            <View className={'flex-1 gap-1'}>
              <Text className={'text-xs text-text-secondary'}>{t('home:frozen')}</Text>
              <Text
                className={'font-roboto-medium text-xs text-text-primary'}
              >{formatLocalFiatAmount(loading ? '−.−−' : summary.frozen.toString())}</Text>
            </View>
          </View>
          <View className={'flex-row gap-2'}>
            <View className={'flex-1 gap-1'}>
              <Text className={'text-xs text-text-secondary'}>{t('home:bonus-balance')}</Text>
              <Text
                className={'font-roboto-medium text-xs text-text-primary'}
              >{formatLocalFiatAmount(loading ? '−.−−' : summary.bonus.toString())}</Text>
            </View>
          </View>
        </View>

        {/* divider */}
        <View className={'h-px bg-border-10'} />

        {/* actions */}
        <View className={'w-full flex-row gap-2 p-4'}>
          {actions.map(({ icon: Icon, ...action }, index) => (
            <Pressable
              key={index}
              className={'flex-1'}
              onPress={() => {
                if (action.pathname) router.navigate(action.pathname as Href)
                else if (action.action) action.action()
              }}
            >
              {({ pressed }) => (
                <View className={cn('items-center gap-2', pressed && 'opacity-50')}>
                  <LinearGradient colors={[brandTheme.DEFAULT, brandTheme.hover]} style={{ borderRadius: 20, padding: 8 }}>
                    <Icon className={'size-6 text-white'} />
                  </LinearGradient>
                  <Text className={'text-sm font-semibold'}>{t(`home:action.${action.method}`)}</Text>
                </View>
              )}
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  )
}

function AssetList({ wallets, loading }: { wallets: Wallet[]; loading: boolean }) {
  const { t } = useTranslation()
  const router = useRouter()

  return (
    <View className={'w-full px-4'}>
      <View className={'mt-4 w-full rounded-lg border border-border-10 bg-white'}>
        {/* header */}
        <View className={'px-4 py-3'}>
          <Text className={'text-sm font-semibold'}>{t('home:assets')}</Text>
        </View>

        {/* divider */}
        <View className={'h-px bg-border-10'} />

        {/* body */}
        <View>
          {SUPPORTED_CURRENCIES.map((currency, index) => {
            const currencyName = CurrencyKind[currency]
            const wallet = wallets.find((w) => w.balance.kind === currencyName)
            const { balance, lockedBalance, frozenBalance, bonusBalance } = wallet || {
              balance: { value: '0' },
              bonusBalance: { value: '0' },
              lockedBalance: { value: '0' },
              frozenBalance: { value: '0' }
            }
            const value: BigNumber = getBignumber(balance.value)
              .add(getBignumber(lockedBalance.value))
              .add(getBignumber(frozenBalance.value))
            const displayValue = toCurrency(value.toString())
            const displayBonusValue = toCurrency(getBignumber(bonusBalance.value).toString())

            return (
              <Fragment key={currency}>
                {index !== 0 && <View className={'ml-4 h-px bg-border-10'} />}
                <Pressable onPress={() => router.navigate(`/asset/${currencyName}`)}>
                  {({ pressed }) => (
                    <View
                      className={cn(
                        'flex-row items-center justify-between gap-2 px-4 py-3',
                        pressed && 'bg-background-secondary'
                      )}
                    >
                      <View className={'flex-row items-center gap-3'}>
                        {/* HACK: Hardcoding the icon for now */}
                        <BrandLogo className={'size-6'} />
                        <Text className={'text-sm font-semibold'}>
                          {currencyName === 'YBB' ? marketPresentation.platformToken.displayName : t(`home:coin.${currencyName}`)}
                        </Text>
                      </View>
                      <View className={'items-end'}>
                        <Text className={'font-roboto-medium text-base text-text-primary'}>
                          {loading ? '−.−−' : displayValue}
                        </Text>
                        <Text className={'text-sm text-text-secondary'}>
                          {formatLocalFiatAmount(loading ? '−.−−' : displayValue)}
                        </Text>
                        <Text className={'text-xs text-text-secondary'}>
                          {t('home:bonus-balance-label', { amount: loading ? '−.−−' : displayBonusValue })}
                        </Text>
                      </View>
                    </View>
                  )}
                </Pressable>
              </Fragment>
            )
          })}
        </View>
      </View>
    </View>
  )
}

function Carousel({ activities }: { activities: any[] }) {
  const { width: windowWidth } = useWindowDimensions()
  const scrollOffsetValue = useSharedValue<number>(0)
  const router = useRouter()
  const width = Math.min(WEB_VIEW_MAX_WIDTH, windowWidth) - 16 - 16
  const height = width * 0.453125
  const selectedIndex = useRef(-1)
  const currentIndex = useRef(-1)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderStart: () => {
      selectedIndex.current = currentIndex.current
    },
    onPanResponderMove: (_, gestureState) => {
      if (Math.abs(gestureState.dx) > 30) {
        selectedIndex.current = -1
      }
    },
    onPanResponderRelease: () => {
      if (selectedIndex.current !== -1) {
        router.push(`/(main)/activity/${ids[selectedIndex.current]}`)
      }
    }
  })

  if (activities.length === 0) {
    return null
  }

  // set the first image as thumbnail
  const images = activities.map(({ node: activity }: { node: Record<string, any> }) => {
    if (activity.picturesBucketPath === null) {
      return ''
    }
    return activity.picturesBucketPath[0]
  })
  const ids = activities.map(({ node: activity }: { node: Record<string, any> }) => activity.id)

  return (
    <View className={'bg-secondary w-full'}>
      <LinearGradient colors={[brandTheme.DEFAULT, basic.gray[5]]}>
        <View className={'h-[100px]'} />
      </LinearGradient>
      <View className={images.length === 0 ? 'mt-[-120px]' : 'mt-[-100px]'} />
      {images.length === 1 && (
        <Pressable className={'mx-4'} onPress={() => router.push(`/(main)/activity/${ids[0]}`)}>
          <View className={'aspect-[1280/580] overflow-hidden rounded-lg bg-background-secondary'}>
            {images[0] !== '' && (
              <Image
                source={{ uri: images[0] }}
                contentFit={'cover'}
                style={{ width: '100%', height: '100%' }}
                transition={200}
              />
            )}
          </View>
        </Pressable>
      )}
      {images.length > 1 && (
        <View>
          <GestureHandlerRootView>
            <RNCarousel
              loop
              scrollOffsetValue={scrollOffsetValue}
              testID={'xxx'}
              style={{ width, height, marginLeft: 16, borderRadius: 8 }}
              orientation={'horizontal'}
              itemSize={width}
              autoplay={true}
              autoplayInterval={7000}
              data={[...new Array(images.length).keys()]}
              snapMode={'page'}
              onSnapToItem={(index) => {
                currentIndex.current = index
                setHighlightedIndex(index)
              }}
              renderItem={({ index }) => (
                <View {...panResponder.panHandlers} key={index}>
                  <View pointerEvents={'none'}>
                    <View className={'overflow-hidden bg-background-secondary'} style={{ height }}>
                      {images[index] !== '' && (
                        <Image
                          source={{ uri: images[index] }}
                          contentFit={'cover'}
                          recyclingKey={ids[index]}
                          style={{ width: '100%', height: '100%' }}
                          transition={200}
                        />
                      )}
                    </View>
                  </View>
                </View>
              )}
            />
          </GestureHandlerRootView>
          <View
            className={'absolute inset-x-0 bottom-3 mx-auto w-fit flex-row flex-wrap justify-center gap-2'}
            style={{ maxWidth: width - 16 - 16 }}
          >
            {images.map((_, index) => (
              <View
                className={cn('size-1.5 rounded-full bg-white', index !== highlightedIndex && 'opacity-50')}
                key={index}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  )
}
